(() => {
  if (window.__sbxFavoritesCoreInitialized) return;
  window.__sbxFavoritesCoreInitialized = true;

  const storageKey = 'socksBoxFavorites';

  const readFavorites = () => {
    try {
      const value = window.localStorage.getItem(storageKey);
      const parsed = value ? JSON.parse(value) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const writeFavorites = (favorites) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(favorites));
    } catch (error) {
      return;
    }
  };

  const getHandle = (button) => button?.dataset?.productHandle || '';

  const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[char]);

  const favoriteUrl = (item) => {
    const raw = item?.url || (item?.handle ? `/products/${item.handle}` : '');
    if (!raw) return '/collections/socks';
    try {
      const url = new URL(raw, window.location.origin);
      if (url.pathname.includes('/products/')) url.searchParams.delete('view');
      return url.toString();
    } catch (error) {
      return raw;
    }
  };

  const updateCartCountBadges = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const count = Number(cart.item_count || 0);
      document.querySelectorAll('.socks-box-global-stack a[href^="/cart"], .socks-box-global-stack a[aria-label^="Cart"], a.sbx-cart-trigger').forEach((link) => {
        let badge = link.querySelector('.sbx-cart-badge');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'sbx-cart-badge';
          link.appendChild(badge);
        }
        badge.textContent = String(count);
        badge.hidden = count <= 0;
        link.setAttribute('aria-label', count > 0 ? `Cart, ${count} items` : 'Cart');
      });
    } catch (error) {
      return;
    }
  };

  const addFavoriteToCart = async (handle, button) => {
    if (!handle || button?.getAttribute('aria-busy') === 'true') return;
    const originalText = button.textContent;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding';

    try {
      const productResponse = await fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
      if (!productResponse.ok) throw new Error('Product lookup failed');
      const product = await productResponse.json();
      const variant = (product.variants || []).find((item) => item.available !== false) || (product.variants || [])[0];
      if (!variant?.id) throw new Error('Variant lookup failed');

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: variant.id, quantity: 1 })
      });
      if (!addResponse.ok) throw new Error('Add to cart failed');

      await updateCartCountBadges();
      button.textContent = 'Added';
      window.dispatchEvent(new CustomEvent('socksbox:cart-change'));
      window.setTimeout(() => {
        if (button.isConnected) button.textContent = originalText || 'Add';
      }, 1400);
    } catch (error) {
      button.textContent = 'View';
      const fallbackUrl = button.dataset.sbxFavoriteUrl;
      if (fallbackUrl) window.location.href = fallbackUrl;
    } finally {
      if (button.isConnected) button.removeAttribute('aria-busy');
    }
  };

  const renderFavoritesPanel = () => {
    const body = document.querySelector('[data-sbx-favorites-panel-body]');
    if (!body) return;
    const favorites = readFavorites();
    if (!favorites.length) {
      body.innerHTML = '<div class="sbx-favorites-panel__empty">Tap the heart on any product to save favorite team socks here.</div>';
      return;
    }
    body.innerHTML = favorites.map((item) => {
      const title = escapeHTML(item.title || 'Saved team socks');
      const handle = escapeHTML(item.handle || '');
      const url = escapeHTML(favoriteUrl(item));
      const image = item.image ? `<img src="${escapeHTML(item.image)}" alt="${title}" loading="lazy">` : '<span class="sbx-favorites-panel__thumb">Saved</span>';
      return `
        <div class="sbx-favorites-panel__item">
          <a href="${url}" aria-label="View ${title}">${image}</a>
          <div class="sbx-favorites-panel__info">
            <a class="sbx-favorites-panel__title" href="${url}"><strong>${title}</strong></a>
            <span>Saved favorite team socks</span>
            <div class="sbx-favorites-panel__actions">
              <a class="sbx-favorites-panel__arrow" href="${url}">View</a>
              <button class="sbx-favorites-panel__add" type="button" data-sbx-favorite-add="${handle}" data-sbx-favorite-url="${url}" aria-label="Add ${title} to cart">Add</button>
              <button class="sbx-favorites-panel__remove" type="button" data-sbx-favorite-remove="${handle}" aria-label="Remove ${title} from favorites">Remove</button>
            </div>
          </div>
        </div>
      `;
    }).join('');
  };

  const setButtonState = (button, isFavorited) => {
    button.classList.toggle('is-favorited', isFavorited);
    button.setAttribute('aria-pressed', isFavorited ? 'true' : 'false');
    const text = button.querySelector('.sbx-favorite-control__text');
    if (text) text.textContent = isFavorited ? 'Saved' : (button.dataset.defaultText || text.textContent || 'Save favorite');
  };

  const refreshButtons = () => {
    const favorites = readFavorites();
    const favoriteHandles = new Set(favorites.map((item) => item.handle).filter(Boolean));
    document.querySelectorAll('[data-sbx-favorite]').forEach((button) => {
      const text = button.querySelector('.sbx-favorite-control__text');
      if (text && !button.dataset.defaultText) button.dataset.defaultText = text.textContent || 'Save favorite';
      setButtonState(button, favoriteHandles.has(getHandle(button)));
    });
    renderFavoritesPanel();
  };

  const toggleFavorite = (button) => {
    const handle = getHandle(button);
    if (!handle) return;

    const favorites = readFavorites();
    const existingIndex = favorites.findIndex((item) => item.handle === handle);
    if (existingIndex >= 0) {
      favorites.splice(existingIndex, 1);
    } else {
      favorites.unshift({
        handle,
        title: button.dataset.productTitle || '',
        url: button.dataset.productUrl || '',
        image: button.dataset.productImage || '',
        savedAt: new Date().toISOString()
      });
    }

    writeFavorites(favorites.slice(0, 100));
    refreshButtons();
    window.dispatchEvent(new CustomEvent('socksbox:favorites-change', { detail: { favorites: readFavorites() } }));
  };

  const removeFavorite = (handle) => {
    if (!handle) return;
    const favorites = readFavorites().filter((item) => item.handle !== handle);
    writeFavorites(favorites);
    refreshButtons();
    window.dispatchEvent(new CustomEvent('socksbox:favorites-change', { detail: { favorites } }));
  };

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-sbx-favorite-add]');
    if (addButton) {
      event.preventDefault();
      event.stopPropagation();
      addFavoriteToCart(addButton.dataset.sbxFavoriteAdd || '', addButton);
      return;
    }

    const removeButton = event.target.closest('[data-sbx-favorite-remove]');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();
      removeFavorite(removeButton.dataset.sbxFavoriteRemove || '');
      return;
    }

    const button = event.target.closest('[data-sbx-favorite]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(button);
  });

  document.addEventListener('keydown', (event) => {
    const button = event.target.closest('[data-sbx-favorite]');
    if (!button) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    toggleFavorite(button);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshButtons, { once: true });
  } else {
    refreshButtons();
  }

  window.SocksBoxFavorites = {
    addFavoriteToCart,
    readFavorites,
    removeFavorite,
    refreshButtons,
    renderFavoritesPanel
  };
})();

